import React, { useContext, useEffect, useState } from 'react'
import { Space, Form, App, Input, Button, Avatar, Upload } from 'antd'
import { Helmet } from 'react-helmet-async';
import { FaLock, FaLockOpen, FaRegUser, FaUserAlt, FaIdCardAlt } from "react-icons/fa";
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { ActionType, API, Enum, Regex } from "../resources/constants";
import language from "../resources/languages/fa_IR";

// Interfaces
import { AxiosError } from "../interfaces/axios"
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

// Contexts
import GlobalContext from "../contexts/global";
import AxiosContext from "../contexts/axios";

const { Pages: { Profile: ProfileLanguage }, Share, Validation, Message: MessageLanguage } = language
const { SET_USER } = ActionType
const { USER_MANAGEMENT_PROFILE_UPDATE, USER_MANAGEMENT_PROFILE_MY_ACCOUNT, USER_MANAGEMENT_PROFILE_UPDATE_AVATAR, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message } = Enum
const { notEnglish, isNumber } = Regex

export default function Profile() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const [uploading, setUploading] = useState(false);
    const { meta, baseURL, user, accessToken, dispatch } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        form.setFieldsValue({ ...user, avatar: undefined })
        form.validateFields().then( response => response ).catch( error => error );
    }, []);

  const beforeUpload = (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      // @ts-ignore
      message.error(ProfileLanguage.avatarFormat).then(response => response );
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      // @ts-ignore
      message.error(ProfileLanguage.avatarSize).then(response => response );
    }
    return isJpgOrPng && isLt2M;
  };

  const onChange = async (info: { file: UploadFile, fileList: UploadFile[] }) => {
    const { file, fileList } = info
    const { status } = file;
    if (status === 'uploading') {
      setUploading(true)
    } else if (status === 'done') {
      axios.get(USER_MANAGEMENT_PROFILE_MY_ACCOUNT).then((response: { data: {} }) => {
        const { data: user } = response
        dispatch({ type: SET_USER , payload: { user }})
        setUploading(false)
        message.success(MessageLanguage[Message.UPLOAD_SUCCESS]).then(response => response)
      }).catch( (error: {}) => error )
    } else if (status === 'error') {
      const { response } = file;
      setUploading(false)
      // @ts-ignore
      message.error(MessageLanguage[response.message ? response.message : Message.UPLOAD_ERROR]).then(response => response)
    }
  };

  const progress: any = () => ({
    strokeColor: {
      '0%': '#108ee9',
      '100%': '#87d068',
    },
    strokeWidth: 3,
    format: (percent: number) => percent && `${parseFloat(percent.toFixed())}%`,
  })

  const onFinish = (values: { firstName: string, lastName: string, nationalCode: string, password?: string, newPassword?: string }) => {
    axios.patch(USER_MANAGEMENT_PROFILE_UPDATE, { ...values }).then((response: {}) => {
      axios.get(USER_MANAGEMENT_PROFILE_MY_ACCOUNT).then((response: { data: {} }) => {
        const { data: user } = response
        dispatch({ type: SET_USER , payload: { user }})
        form.resetFields(['password', 'newPassword', 'confirm'])
        form.setFieldsValue({ ...user, avatar: undefined })
        message.success(MessageLanguage[Message.UPDATE_PROFILE]).then(response => response)
      }).catch( (error: {}) => error )
    }, (error: AxiosError) => {
      const { response: { data } } = error;
      // @ts-ignore
      message.error(MessageLanguage[data.message]).then(response => response)
    })
  };

    return (
      <>
        { meta?.title && (
          <Helmet>
            <title>{ ProfileLanguage.profile + ' - ' + meta.title }</title>
          </Helmet>
        )
        }
        <Form form={form} name="profile" autoComplete="off" onFinish={onFinish} style={{ maxWidth: 400, margin: 'auto' }}>
          <Space direction="vertical" size="middle" style={{width:'100%'}}>
            <Form.Item
              name="avatar"
            >
              <Upload.Dragger
                name="file"
                action={baseURL + USER_MANAGEMENT_PROFILE_UPDATE_AVATAR}
                headers={{ Authorization: `Bearer ${accessToken}` }}
                multiple={false}
                showUploadList={false}
                disabled={uploading}
                beforeUpload={beforeUpload}
                onChange={onChange}
                progress={progress}
              >
                <p className="ant-upload-drag-icon"><Avatar size={90} icon={user?.avatar ? <img
                  src={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + user.avatar + `?width=90&height=90&token=${accessToken}`}
                  width={90} /> : <FaRegUser size={40} />} /></p>
                <p className="ant-upload-text">{ProfileLanguage.uploadTitle}</p>
                <p className="ant-upload-hint">{ProfileLanguage.uploadDescription}</p>
              </Upload.Dragger>
            </Form.Item>
            <Form.Item
              name="firstName"
              hasFeedback
              rules={[
                { required: true, message: `${ProfileLanguage.firstName} ${Validation.required}` },
                { min: 3, message: `${ProfileLanguage.firstName} ${Validation.min} 3 ${Validation.length}` },
                { max: 30, message: `${ProfileLanguage.firstName} ${Validation.max} 30 ${Validation.length}` },
                { pattern: notEnglish, message: Validation.persian },
              ]}
            >
              <Input minLength={3} prefix={<FaUserAlt size={13} />} placeholder={ProfileLanguage.firstName} />
            </Form.Item>
            <Form.Item
              name="lastName"
              hasFeedback
              rules={[
                { required: true, message: `${ProfileLanguage.lastName} ${Validation.required}` },
                { min: 3, message: `${ProfileLanguage.lastName} ${Validation.min} 3 ${Validation.length}` },
                { max: 30, message: `${ProfileLanguage.lastName} ${Validation.max} 30 ${Validation.length}` },
                { pattern: notEnglish, message: Validation.persian },
              ]}
            >
              <Input minLength={3} prefix={<FaUserAlt size={13} />} placeholder={ProfileLanguage.lastName} />
            </Form.Item>
              <Form.Item
                  name="nationalCode"
                  hasFeedback
                  rules={[
                      { pattern: isNumber, message: `${ProfileLanguage.nationalCode} ${Validation.pattern}` },
                      { len: 10, message: `${ProfileLanguage.nationalCode} 10 ${Validation.length}` },
                  ]}
              >
                  <Input type="tel" maxLength={10} prefix={<FaIdCardAlt size={13} />} placeholder={ProfileLanguage.nationalCode} />
              </Form.Item>
            <Form.Item
              name="password"
              hasFeedback
              dependencies={['newPassword']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || !getFieldValue('newPassword')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(`${ProfileLanguage.password} ${Validation.required}`));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<FaLockOpen />} placeholder={ProfileLanguage.password} />
            </Form.Item>
            <Form.Item
              name="newPassword"
              hasFeedback
              dependencies={['password']}
              rules={[
                { min: 8, message: `${ProfileLanguage.password} ${Validation.min} 8 ${Validation.length}` },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || !getFieldValue('password')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(`${ProfileLanguage.newPassword} ${Validation.required}`));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<FaLock />} placeholder={ProfileLanguage.newPassword} />
            </Form.Item>
            <Form.Item
              name="confirm"
              hasFeedback
              dependencies={['newPassword']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if ((!value && !getFieldValue('newPassword')) || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(Validation.newPasswordConfirm));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<FaLock />} placeholder={ProfileLanguage.confirm} />
            </Form.Item>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type="primary"
                  block
                  htmlType="submit"
                  disabled={!form.getFieldValue('firstName') || !form.getFieldValue('lastName') || !form.getFieldValue('nationalCode') || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                >
                  { Share.edit }
                </Button>
              )}
            </Form.Item>
          </Space>
        </Form>
      </>
    )
}
